/*
 * The live Orders card's visit context (hubfix-live-orders, owner 10-23): what placing an order for the recorded
 * visit needs (practice, patient, the visit row's order body fields), registered by the card in the chart's
 * LiveScribe panel and kept for the session, so the end-of-visit review can place its leftovers even when the
 * CareIQ tab was never opened (live 10-22/23: the review did not open on 28 visits because only that tab opened it).
 * Placement = N3/orderCreate.js: the same orders/create endpoint and body as CareIQ's Add; Undo = the same delete.
 */
import { eventBus } from "../../utils/eventBus";
import { visitKeyOf } from "./orderReviewModel";
import { FALLBACK_ORDER_ALIASES, mapOrderType } from "../../landingscreen/patientcharts/tabs/careiq/N3/orderKey";
import {
  changeVisitOrderWhen, createVisitOrder, deleteVisitOrder, fetchVisitOrders, orderCreateBody, placedWhenEditable,
} from "../../landingscreen/patientcharts/tabs/careiq/N3/orderCreate";

const visits = new Map();     // visitKey -> { practiceId, patientId, apptKey, ctx, canOrder }
const mounted = new Map();    // visitKey -> number of mounted cards

export const registerLiveVisit = (visitKey, info) => {
  if (visitKey && info && info.practiceId && info.patientId && info.ctx) visits.set(String(visitKey), info);
};
export const liveVisit = (visitKey) => (visitKey ? visits.get(String(visitKey)) || null : null);
export const cardMounted = (visitKey, delta) => {
  const k = String(visitKey || "");
  if (!k) return 0;
  if (delta) mounted.set(k, Math.max(0, (mounted.get(k) || 0) + delta));
  return mounted.get(k) || 0;
};

/* Review 10-23: placements in flight, per visit + concept, for the whole page (the card is mounted in three
   exclusive LiveScribePanel branches, so a tap's state must survive a remount, and two mounted cards / the review
   must never place the same row twice). Every change emits "liveOrdersInflight" so mounted cards re-render. */
const inflight = new Set();
const failedRows = new Set();
const flightKey = (visitKey, concept) => `${visitKey || ""}|${concept || ""}`;
export const isInflight = (visitKey, concept) => inflight.has(flightKey(visitKey, concept));
export const isFailed = (visitKey, concept) => failedRows.has(flightKey(visitKey, concept));
// true = this caller owns the slot (false = another tap / card is already placing it)
export const beginFlight = (visitKey, concept) => {
  const k = flightKey(visitKey, concept);
  if (inflight.has(k)) return false;
  inflight.add(k);
  failedRows.delete(k);
  eventBus.emit("liveOrdersInflight", { visitKey, concept });
  return true;
};
export const endFlight = (visitKey, concept, failed = false) => {
  const k = flightKey(visitKey, concept);
  inflight.delete(k);
  if (failed) failedRows.add(k); else failedRows.delete(k);
  eventBus.emit("liveOrdersInflight", { visitKey, concept });
};

/* Wait (re-review 10-23) until none of `concepts` is being placed - in this page (beginFlight) or in another tab
   (the record's liveInflight marker, read by readMarkers()) - or until timeoutMs. Resolves the concepts still in
   flight at the end ([] = all settled). */
let WAIT_POLL_MS = 150;
export const setFlightPoll = (ms) => { WAIT_POLL_MS = Number(ms) > 0 ? Number(ms) : 150; };
export const waitFlights = (visitKey, concepts, { timeoutMs = 15000, inOtherTab = () => false } = {}) => new Promise((resolve) => {
  const pending = () => (concepts || []).filter((c) => c && (isInflight(visitKey, c) || inOtherTab(c)));
  let waited = 0;                                 // counted in polls (no wall clock: works under a pinned Date)
  const tick = () => {
    const left = pending();
    if (!left.length || waited >= timeoutMs) {
      resolve(left);
      return;
    }
    waited += WAIT_POLL_MS;
    setTimeout(tick, WAIT_POLL_MS);
  };
  tick();
});

/* hubfix-oi-1026 (owner 10-26): the card is also shown in the Orders popup / page and on the dashboard's expanded
   card. live_surface is sent once per visit + surface per page load; a surface's card hidden with its ✕ stays hidden
   (page-wide, survives the dashboard's re-renders) until a row arrives that was not open when it was hidden. */
const surfacesSent = new Set();
const surfaceHidden = new Map();   // `${visitKey}|${surface}` -> Set of the open concepts when hidden
export const markSurface = (visitKey, surface) => {
  const k = `${visitKey || ""}|${surface || ""}`;
  if (!visitKey || surfacesSent.has(k)) return false;
  surfacesSent.add(k);
  return true;
};
export const hideSurface = (visitKey, surface, concepts) => {
  if (visitKey) surfaceHidden.set(`${visitKey}|${surface || ""}`, new Set(concepts || []));
};
export const surfaceIsHidden = (visitKey, surface, openConcepts) => {
  const h = surfaceHidden.get(`${visitKey || ""}|${surface || ""}`);
  if (!h) return false;
  return (openConcepts || []).every((c) => h.has(c));
};

/* Review 10-26 (dashboard load): at most ONE orders/get per visit at a time page-wide, whatever the number of mounted
   cards (chart + Orders popup + dashboard): concurrent reads of a visit share one request, and the result is broadcast
   ("liveVisitOrders") to every card of the visit. The 20 s poll runs in ONE card per visit (claimPoll: the first
   visible card with open rows; the next one takes over when it unmounts / hides).
   Re-review 10-26: the read has a READ_TIMEOUT_MS axios timeout, and a read in flight is joined for at most
   JOIN_MAX_MS (a hung request never freezes the visit's card); after a placement / Undo (changed()) the next read is
   a FRESH one, never the one already in flight (it may predate the change). */
export const READ_TIMEOUT_MS = 10000;
export const JOIN_MAX_MS = 15000;
const sharedReads = new Map();   // visitKey -> { p, at }
const pollOwner = new Map();     // visitKey -> token
let mono = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
// tests only: the clock the join window uses
export const setReadClock = (fn) => { mono = typeof fn === "function" ? fn : () => performance.now(); };
export const sharedVisitOrders = (visitKey, info, { fresh = false } = {}) => {
  const k = String(visitKey || "");
  if (!k || !info) return Promise.resolve(null);
  const cur = sharedReads.get(k);
  if (cur && !fresh && mono() - cur.at < JOIN_MAX_MS) return cur.p;
  const entry = { at: mono(), p: null };
  entry.p = Promise.resolve()
    .then(() => visitOrdersOf(info, { timeoutMs: READ_TIMEOUT_MS }))
    .catch(() => null)
    .then((list) => {
      if (sharedReads.get(k) === entry) sharedReads.delete(k);      // a newer read may have replaced it
      if (Array.isArray(list)) eventBus.emit("liveVisitOrders", { visitKey: k, list });
      return list;
    });
  sharedReads.set(k, entry);
  return entry.p;
};
// the visit's orders changed here: the read in flight is stale (the next sharedVisitOrders starts a fresh one)
export const invalidateVisitOrders = (visitKey) => { sharedReads.delete(String(visitKey || "")); };
export const claimPoll = (visitKey, token) => {
  const k = String(visitKey || "");
  if (!k) return false;
  const cur = pollOwner.get(k);
  if (cur == null || cur === token) {
    pollOwner.set(k, token);
    return true;
  }
  return false;
};
export const releasePoll = (visitKey, token) => {
  const k = String(visitKey || "");
  if (pollOwner.get(k) === token) pollOwner.delete(k);
};

// tests only
export const resetLiveVisits = () => {
  visits.clear(); mounted.clear(); inflight.clear(); failedRows.clear(); surfacesSent.clear(); surfaceHidden.clear();
  sharedReads.clear(); pollOwner.clear();
};

const changed = (info, delta, orderId) => {
  if (info) invalidateVisitOrders(visitKeyOf(String(info.patientId), String(info.apptKey || "")));
  // the same signals CareIQ's Add / Undo send: the CareIQ tab and the Orders panel re-read the visit's orders
  eventBus.emit("fetchSummary");
  eventBus.emit("refreshDashboard");
  // hubfix-oi-1026: the Orders popup re-reads its list and counts the card's order as saved (it is placed confirmed)
  if (info) eventBus.emit("liveOrderChanged", { patientId: String(info.patientId), apptKey: String(info.apptKey || ""), delta, orderId });
};

// Place one row -> { orderId, ordertype } (orderId null = not placed).
export const placeLiveRow = async (info, row) => {
  if (!info || info.canOrder === false || !row || !row.order_name) return { orderId: null, ordertype: null };
  const ordertype = mapOrderType(row.order_type, row.order_name);
  const body = orderCreateBody(info.ctx, row.order_name, ordertype, [], { when: row.when });   // Today / Schedule (10-24)
  const orderId = await createVisitOrder(info.practiceId, info.patientId, body);
  if (orderId != null) changed(info, 1, orderId);
  return { orderId, ordertype };
};

export const undoLiveOrder = async (info, { orderId, ordertype }) => {
  if (!info || orderId == null) return false;
  const ok = await deleteVisitOrder(info.practiceId, { orderId, orderDate: info.ctx.orderDate, patientId: info.patientId, ordertype });
  if (ok) changed(info, -1, orderId);
  return ok;
};

export const visitOrdersOf = (info, opts) => (info ? fetchVisitOrders(info.practiceId, info.patientId, info.ctx.orderDate, opts) : Promise.resolve(null));

// Today <-> Schedule of a placed order of this visit (scoped to the visit's orders, read back) -> { ok, error }.
export const changeLiveOrderWhen = async (info, orderId, when) => {
  if (!info || orderId == null) return { ok: false, error: "Not changed" };
  const r = await changeVisitOrderWhen({
    practiceId: info.practiceId, me: info.ctx && info.ctx.me, patientId: info.patientId,
    orderDate: info.ctx && info.ctx.orderDate, fetchOrders: () => visitOrdersOf(info),
  }, orderId, when);
  if (r.ok) changed(info, 0, orderId);
  return r;
};

// A review context (OrderReviewHost registerOrderReviewVisit shape) placing through the same path.
export const reviewContextFor = (info, payload) => ({
  payload,
  aliases: FALLBACK_ORDER_ALIASES,
  place: async (row) => (await placeLiveRow(info, { order_name: row.name, order_type: row.order_type, when: row.when })).orderId != null,
  fetchOrders: async () => (await visitOrdersOf(info)) || [],
  setWhen: (orderId, when) => changeLiveOrderWhen(info, orderId, when),
  whenEditable: placedWhenEditable(info && info.ctx && info.ctx.orderDate),
});
